import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, Typography, Table, Tag, Spin, Card, Collapse, Toast, Space, Tabs, DatePicker, Banner, Row, Col } from '@douyinfe/semi-ui';
import { IconSearch, IconCopy, IconDownload } from '@douyinfe/semi-icons';
import { API, timestamp2string } from '../helpers';
import { stringToColor } from '../helpers/render';
import { renderModelPrice, renderQuota } from '../helpers/render';
import Paragraph from '@douyinfe/semi-ui/lib/es/typography/paragraph';
import { Tooltip, Modal } from '@douyinfe/semi-ui';
import Papa from 'papaparse';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    Legend, ResponsiveContainer, LineChart, Line
} from 'recharts';

const { Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

function renderTimestamp(timestamp) {
    return timestamp2string(timestamp);
}

function renderIsStream(bool) {
    if (bool) {
        return <Tag color="blue" size="large">流</Tag>;
    } else {
        return <Tag color="purple" size="large">非流</Tag>;
    }
}

function renderUseTime(type) {
    const time = parseInt(type);
    if (time < 101) {
        return <Tag color="green" size="large"> {time} 秒 </Tag>;
    } else if (time < 300) {
        return <Tag color="orange" size="large"> {time} 秒 </Tag>;
    } else {
        return <Tag color="red" size="large"> {time} 秒 </Tag>;
    }
}

function formatTokens(value) {
    const v = Number(value) || 0;
    if (v >= 10000) {
        return `${(v / 10000).toFixed(2)}万`;
    }
    return `${v}`;
}

function parseLogOther(other) {
    if (!other) return {};
    if (typeof other === 'object') return other;
    try {
        return JSON.parse(other);
    } catch (e) {
        return {};
    }
}

function getCacheTokens(log) {
    if (typeof log.cache_tokens === 'number') return log.cache_tokens;
    if (typeof log.cacheTokens === 'number') return log.cacheTokens;
    const otherObj = parseLogOther(log.other);
    return otherObj.cache_tokens || 0;
}

function normalizeLog(log) {
    const cacheTokens = getCacheTokens(log);
    return {
        ...log,
        cacheTokens,
    };
}

function sortLogsDesc(a, b) {
    const idDiff = (b.id || 0) - (a.id || 0);
    if (idDiff !== 0) return idDiff;
    return (b.created_at || 0) - (a.created_at || 0);
}

function isInTimeRange(createdAt, startTs, endTs) {
    if (startTs && createdAt < startTs) return false;
    if (endTs && createdAt > endTs) return false;
    return true;
}

function getStartOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function getEndOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function toTimestamp(date) {
    return Math.floor(date.getTime() / 1000);
}

function toDayString(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getPresetRange(presetKey) {
    const now = new Date();
    const end = getEndOfDay(now);
    if (presetKey === 'all') {
        return {
            quickRange: 'all',
            dateRange: [],
            startTs: 0,
            endTs: 0,
        };
    }
    const start = getStartOfDay(now);
    const dayCount = {
        today: 1,
        '7d': 7,
        '30d': 30,
        '90d': 90,
    }[presetKey] || 7;
    start.setDate(start.getDate() - (dayCount - 1));
    return {
        quickRange: presetKey,
        dateRange: [start, end],
        startTs: toTimestamp(start),
        endTs: toTimestamp(end),
    };
}

function createDefaultFilter() {
    return getPresetRange('7d');
}

function getEmptyTotals() {
    return {
        prompt_tokens: 0,
        completion_tokens: 0,
        cache_tokens: 0,
        total_tokens: 0,
        quota: 0,
        cost: 0,
        calls: 0,
    };
}

function createInitialTabState() {
    return {
        totalGranted: 0,
        totalUsed: 0,
        totalAvailable: 0,
        unlimitedQuota: false,
        expiresAt: 0,
        tokenName: '',
        logs: [],
        logsTotal: 0,
        logsPage: 1,
        logsPageSize: 10,
        tokenValid: false,
        daily: [],
        totals: getEmptyTotals(),
        useFallback: false,
        filter: createDefaultFilter(),
    };
}

function aggregateByDay(logs, startTs = 0, endTs = 0) {
    const dayMap = {};
    logs.forEach((log) => {
        if (log.type !== 0 && log.type !== 2) return;
        if (!isInTimeRange(log.created_at, startTs, endTs)) return;
        const date = new Date(log.created_at * 1000);
        const day = toDayString(date);
        if (!dayMap[day]) {
            dayMap[day] = {
                date: day,
                prompt_tokens: 0,
                completion_tokens: 0,
                cache_tokens: 0,
                total_tokens: 0,
                quota: 0,
                cost: 0,
                calls: 0,
            };
        }
        const p = log.prompt_tokens || 0;
        const c = log.completion_tokens || 0;
        const ca = getCacheTokens(log);
        const q = log.quota || 0;
        dayMap[day].prompt_tokens += p;
        dayMap[day].completion_tokens += c;
        dayMap[day].cache_tokens += ca;
        dayMap[day].total_tokens += p + c + ca;
        dayMap[day].quota += q;
        dayMap[day].cost += q / 500000;
        dayMap[day].calls += 1;
    });
    return Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date));
}

function fillMissingDays(rows, startTs, endTs) {
    if (!startTs || !endTs) return rows;
    const rowMap = {};
    rows.forEach((row) => {
        rowMap[row.date] = row;
    });
    const result = [];
    const current = getStartOfDay(new Date(startTs * 1000));
    const end = getStartOfDay(new Date(endTs * 1000));
    while (current <= end) {
        const day = toDayString(current);
        result.push(rowMap[day] || {
            date: day,
            prompt_tokens: 0,
            completion_tokens: 0,
            cache_tokens: 0,
            total_tokens: 0,
            quota: 0,
            cost: 0,
            calls: 0,
        });
        current.setDate(current.getDate() + 1);
    }
    return result;
}

function computeTotalsFromDaily(daily) {
    return daily.reduce((acc, row) => ({
        prompt_tokens: acc.prompt_tokens + (row.prompt_tokens || 0),
        completion_tokens: acc.completion_tokens + (row.completion_tokens || 0),
        cache_tokens: acc.cache_tokens + (row.cache_tokens || 0),
        total_tokens: acc.total_tokens + (row.total_tokens || 0),
        quota: acc.quota + (row.quota || 0),
        cost: acc.cost + (row.cost || 0),
        calls: acc.calls + (row.calls || 0),
    }), getEmptyTotals());
}

async function fetchLogsPage(baseUrl, apikey, startTs, endTs, page, pageSize) {
    const res = await API.get(`${baseUrl}/api/log/token/range`, {
        headers: { Authorization: 'Bearer ' + apikey },
        params: {
            start_timestamp: startTs || 0,
            end_timestamp: endTs || 0,
            page,
            page_size: pageSize,
        },
    });
    const { success, data } = res.data || {};
    if (!success || !data) {
        return { items: [], total: 0, success: false };
    }
    return {
        items: data.items || [],
        total: data.total || 0,
        success: true,
    };
}

const LogsTable = () => {
    const [apikey, setAPIKey] = useState('');
    const [activeTabKey, setActiveTabKey] = useState('');
    const [tabData, setTabData] = useState({});
    const [loading, setLoading] = useState(false);
    const [activeKeys, setActiveKeys] = useState([]);
    const [hiddenLinesDetailChart, setHiddenLinesDetailChart] = useState({});
    const [hiddenLinesStatsChart, setHiddenLinesStatsChart] = useState({});
    const [baseUrl, setBaseUrl] = useState('');
    const baseUrls = useMemo(() => JSON.parse(process.env.REACT_APP_BASE_URL), []);

    useEffect(() => {
        const firstKey = Object.keys(baseUrls)[0];
        setActiveTabKey(firstKey);
        setBaseUrl(baseUrls[firstKey]);
        setTabData((prevData) => ({
            ...prevData,
            [firstKey]: prevData[firstKey] || createInitialTabState(),
        }));
    }, [baseUrls]);

    const getTabState = (data, key) => data[key] || createInitialTabState();
    const toggleLine = (setter) => (entry) => {
        const key = entry && entry.dataKey;
        if (!key) return;
        setter((prev) => ({ ...prev, [key]: !prev[key] }));
    };
    const legendFormatter = (hiddenLines) => (value, entry) => {
        const key = entry && entry.dataKey;
        const isHidden = !!(key && hiddenLines[key]);
        return (
            <span style={{ color: isHidden ? '#bbb' : undefined, cursor: 'pointer' }}>
                {value}
            </span>
        );
    };

    const handleTabChange = (key) => {
        setActiveTabKey(key);
        setBaseUrl(baseUrls[key]);
        setTabData((prevData) => ({
            ...prevData,
            [key]: prevData[key] || createInitialTabState(),
        }));
    };

    const resetData = (key) => {
        const existing = tabData[key] || createInitialTabState();
        setTabData((prevData) => ({
            ...prevData,
            [key]: {
                ...createInitialTabState(),
                filter: existing.filter,
            }
        }));
    };

    const setQuickRange = (presetKey) => {
        setTabData((prevData) => {
            const current = getTabState(prevData, activeTabKey);
            return {
                ...prevData,
                [activeTabKey]: {
                    ...current,
                    filter: getPresetRange(presetKey),
                },
            };
        });
    };

    const handleDateRangeChange = (value) => {
        let startTs = 0;
        let endTs = 0;
        let dateRange = value || [];
        if (Array.isArray(value) && value[0] && value[1]) {
            const startDate = getStartOfDay(new Date(value[0]));
            const endDate = getEndOfDay(new Date(value[1]));
            startTs = toTimestamp(startDate);
            endTs = toTimestamp(endDate);
            dateRange = [startDate, endDate];
        }
        setTabData((prevData) => {
            const current = getTabState(prevData, activeTabKey);
            return {
                ...prevData,
                [activeTabKey]: {
                    ...current,
                    filter: {
                        quickRange: 'custom',
                        dateRange,
                        startTs,
                        endTs,
                    },
                },
            };
        });
    };

    const fetchData = async () => {
        if (apikey === '') {
            Toast.warning('请先输入令牌，再进行查询');
            return;
        }
        if (!/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) {
            Toast.error('令牌格式非法！');
            return;
        }
        setLoading(true);
        const currentTabData = tabData[activeTabKey] || createInitialTabState();
        const currentFilter = currentTabData.filter || createDefaultFilter();
        const startTs = currentFilter.startTs || 0;
        const endTs = currentFilter.endTs || 0;
        const currentPageSize = currentTabData.logsPageSize || 10;
        let newTabData = {
            ...createInitialTabState(),
            ...currentTabData,
            totalGranted: 0,
            totalUsed: 0,
            totalAvailable: 0,
            unlimitedQuota: false,
            expiresAt: 0,
            tokenName: '',
            logs: [],
            logsTotal: 0,
            logsPage: 1,
            logsPageSize: currentPageSize,
            tokenValid: false,
            daily: [],
            totals: getEmptyTotals(),
            useFallback: false,
            filter: currentFilter,
        };

        try {
            if (process.env.REACT_APP_SHOW_BALANCE === "true") {
                const usageRes = await API.get(`${baseUrl}/api/usage/token/`, {
                    headers: { Authorization: 'Bearer ' + apikey },
                });
                const usageData = usageRes.data;
                if (usageData.code) {
                    const d = usageData.data;
                    newTabData.unlimitedQuota = d.unlimited_quota;
                    newTabData.totalGranted = d.total_granted;
                    newTabData.totalUsed = d.total_used;
                    newTabData.totalAvailable = d.total_available;
                    newTabData.expiresAt = d.expires_at;
                    newTabData.tokenName = d.name;
                    newTabData.tokenValid = true;
                } else {
                    Toast.error(usageData.message || '查询令牌信息失败');
                }
            }
        } catch (e) {
            console.log(e);
            Toast.error('查询令牌信息失败，请检查令牌是否正确');
            resetData(activeTabKey);
            setLoading(false);
            return;
        }

        try {
            if (process.env.REACT_APP_SHOW_DETAIL === "true") {
                let useFallback = false;
                try {
                    const dailyRes = await API.get(`${baseUrl}/api/log/token/daily`, {
                        headers: { Authorization: 'Bearer ' + apikey },
                        params: {
                            start_timestamp: startTs || 0,
                            end_timestamp: endTs || 0,
                            timezone: 'Asia/Shanghai',
                        },
                    });
                    const { success, data } = dailyRes.data || {};
                    if (success && data) {
                        newTabData.daily = data.items || [];
                        newTabData.totals = data.totals || getEmptyTotals();
                    } else {
                        useFallback = true;
                    }
                } catch (e) {
                    useFallback = true;
                }

                if (!useFallback) {
                    const { items, total, success } = await fetchLogsPage(
                        baseUrl, apikey, startTs, endTs, 1, currentPageSize
                    );
                    if (!success) {
                        Toast.error('查询失败，请输入正确的令牌');
                        resetData(activeTabKey);
                        setLoading(false);
                        return;
                    }
                    const normalizedLogs = items
                        .map(normalizeLog)
                        .sort(sortLogsDesc);
                    newTabData.logs = normalizedLogs;
                    newTabData.logsTotal = total;
                    newTabData.logsPage = 1;
                    newTabData.logsPageSize = currentPageSize;
                    newTabData.tokenValid = true;
                } else {
                    const res = await API.get(`${baseUrl}/api/log/token`, {
                        headers: { Authorization: 'Bearer ' + apikey },
                    });
                    const logs = (res.data && res.data.data) || [];
                    const normalizedLogs = logs
                        .map(normalizeLog)
                        .filter((log) => isInTimeRange(log.created_at, startTs, endTs))
                        .sort(sortLogsDesc);
                    const fallbackDaily = fillMissingDays(aggregateByDay(normalizedLogs, startTs, endTs), startTs, endTs);
                    newTabData.logs = normalizedLogs;
                    newTabData.daily = fallbackDaily;
                    newTabData.totals = computeTotalsFromDaily(fallbackDaily);
                    newTabData.useFallback = true;
                    newTabData.tokenValid = true;
                }
                setActiveKeys(['1', '2', '3']);
            }
        } catch (e) {
            Toast.error('查询失败，请输入正确的令牌');
            resetData(activeTabKey);
            setLoading(false);
            return;
        }

        setTabData((prevData) => ({
            ...prevData,
            [activeTabKey]: newTabData,
        }));
        setLoading(false);
    };

    const loadLogsPage = async (page, newPageSize) => {
        if (!apikey || !/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) return;
        const currentTabData = tabData[activeTabKey] || createInitialTabState();

        if (currentTabData.useFallback) {
            setTabData((prevData) => ({
                ...prevData,
                [activeTabKey]: {
                    ...currentTabData,
                    logsPage: page,
                    logsPageSize: newPageSize,
                },
            }));
            return;
        }

        const filter = currentTabData.filter || createDefaultFilter();
        setLoading(true);
        try {
            const { items, total, success } = await fetchLogsPage(
                baseUrl, apikey, filter.startTs || 0, filter.endTs || 0, page, newPageSize
            );
            if (!success) {
                Toast.error('拉取明细失败');
                return;
            }
            const normalizedLogs = items.map(normalizeLog).sort(sortLogsDesc);
            setTabData((prevData) => ({
                ...prevData,
                [activeTabKey]: {
                    ...currentTabData,
                    logs: normalizedLogs,
                    logsTotal: total,
                    logsPage: page,
                    logsPageSize: newPageSize,
                },
            }));
        } catch (e) {
            Toast.error('拉取明细失败');
        } finally {
            setLoading(false);
        }
    };

    const copyText = async (text) => {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                Toast.success('已复制：' + text);
                return;
            }
            
            // Fallback for Safari and older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                document.execCommand('copy');
                textArea.remove();
                Toast.success('已复制：' + text);
            } catch (err) {
                textArea.remove();
                Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
            }
        } catch (err) {
            Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
        }
    };

    const columns = [
        {
            title: '时间',
            dataIndex: 'created_at',
            render: renderTimestamp,
            sorter: (a, b) => a.created_at - b.created_at,
            defaultSortOrder: 'descend',
        },
        {
            title: '令牌名称',
            dataIndex: 'token_name',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? (
                    <div>
                        <Tag
                            color="grey"
                            size="large"
                            onClick={() => {
                                copyText(text);
                            }}
                        >
                            {' '}
                            {text}{' '}
                        </Tag>
                    </div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => ('' + a.token_name).localeCompare(b.token_name),
        },
        {
            title: '模型',
            dataIndex: 'model_name',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? (
                    <div>
                        <Tag
                            color={stringToColor(text)}
                            size="large"
                            onClick={() => {
                                copyText(text);
                            }}
                        >
                            {' '}
                            {text}{' '}
                        </Tag>
                    </div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => ('' + a.model_name).localeCompare(b.model_name),
        },
        {
            title: '用时',
            dataIndex: 'use_time',
            render: (text, record, index) => {
                return record.model_name.startsWith('mj_') ? null : (
                    <div>
                        <Space>
                            {renderUseTime(text)}
                            {renderIsStream(record.is_stream)}
                        </Space>
                    </div>
                );
            },
            sorter: (a, b) => a.use_time - b.use_time,
        },
        {
            title: '输入token',
            dataIndex: 'prompt_tokens',
            render: (text, record, index) => {
                return record.model_name.startsWith('mj_') ? null : (
                    record.type === 0 || record.type === 2 ? <div>{<span> {(text / 10000).toFixed(2)}万 </span>}</div> : <></>
                );
            },
            sorter: (a, b) => a.prompt_tokens - b.prompt_tokens,
        },
        {
            title: '输出token',
            dataIndex: 'completion_tokens',
            render: (text, record, index) => {
                return parseInt(text) > 0 && (record.type === 0 || record.type === 2) ? (
                    <div>{<span> {(text / 10000).toFixed(2)}万 </span>}</div>
                ) : (
                    <></>
                );
            },
            sorter: (a, b) => a.completion_tokens - b.completion_tokens,
        },
        {
            title: '缓存',
            dataIndex: 'cacheTokens',
            render: (text, record, index) => {
                if (record.type !== 0 && record.type !== 2) return <></>;
                return text > 0 ? <div>{<span> {(text / 10000).toFixed(2)}万 </span>}</div> : <></>;
            },
            sorter: (a, b) => getCacheTokens(a) - getCacheTokens(b),
        },
        {
            title: '花费',
            dataIndex: 'quota',
            render: (text, record, index) => {
                return record.type === 0 || record.type === 2 ? <div>{renderQuota(text, 6)}</div> : <></>;
            },
            sorter: (a, b) => a.quota - b.quota,
        },
        {
            title: '详情',
            dataIndex: 'content',
            render: (text, record, index) => {
                let other = null;
                try {
                    if (record.other === '') {
                        record.other = '{}';
                    }
                    other = JSON.parse(record.other);
                } catch (e) {
                    return (
                        <Tooltip content="该版本不支持显示计算详情">
                            <Paragraph
                                ellipsis={{
                                    rows: 2,
                                }}
                            >
                                {text}
                            </Paragraph>
                        </Tooltip>
                    );
                }
                if (other == null) {
                    return (
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                                showTooltip: {
                                    type: 'popover',
                                },
                            }}
                        >
                            {text}
                        </Paragraph>
                    );
                }
                let content = renderModelPrice(
                    record.prompt_tokens,
                    record.completion_tokens,
                    other.model_ratio,
                    other.model_price,
                    other.completion_ratio,
                    other.group_ratio,
                );
                return (
                    <Tooltip content={content}>
                        <Paragraph
                            ellipsis={{
                                rows: 2,
                            }}
                        >
                            {text}
                        </Paragraph>
                    </Tooltip>
                );
            },
        }
    ];

    const copyTokenInfo = (e) => {
        e.stopPropagation();
        const activeTabData = tabData[activeTabKey] || {};
        const { totalGranted, totalUsed, totalAvailable, unlimitedQuota, expiresAt, tokenName } = activeTabData;
        const info = `令牌名称: ${tokenName || '未知'}
令牌总额: ${unlimitedQuota ? '无限' : renderQuota(totalGranted, 3)}
剩余额度: ${unlimitedQuota ? '无限制' : renderQuota(totalAvailable, 3)}
已用额度: ${unlimitedQuota ? '不进行计算' : renderQuota(totalUsed, 3)}
有效期至: ${expiresAt === 0 ? '永不过期' : renderTimestamp(expiresAt)}`;
        copyText(info);
    };

    const exportCSV = async (e) => {
        e.stopPropagation();
        const activeTabData = tabData[activeTabKey] || { logs: [] };
        try {
            let logs = activeTabData.logs || [];
            if (!activeTabData.useFallback && (activeTabData.logsTotal || 0) > logs.length) {
                const filter = activeTabData.filter || createDefaultFilter();
                const pageSize = 200;
                const maxPages = 500;
                let expectedTotal = activeTabData.logsTotal || 0;
                let page = 1;
                let allLogs = [];
                while (page <= maxPages) {
                    const { items, total, success } = await fetchLogsPage(
                        baseUrl,
                        apikey,
                        filter.startTs || 0,
                        filter.endTs || 0,
                        page,
                        pageSize
                    );
                    if (!success) {
                        Toast.error('导出失败，请稍后重试');
                        return;
                    }
                    expectedTotal = total;
                    allLogs = allLogs.concat(items.map(normalizeLog));
                    if (items.length < pageSize || allLogs.length >= total) {
                        break;
                    }
                    page += 1;
                }
                if (page > maxPages && allLogs.length < expectedTotal) {
                    Toast.warning('导出数据已达到最大分页上限，结果可能不完整');
                }
                logs = allLogs.sort(sortLogsDesc);
            }
            const csvData = logs.map(log => ({
                '时间': renderTimestamp(log.created_at),
                '模型': log.model_name,
                '用时': log.use_time,
                '输入token': log.prompt_tokens,
                '输出token': log.completion_tokens,
                '缓存': getCacheTokens(log),
                '花费': log.quota,
                '详情': log.content,
            }));
            const csvString = '\ufeff' + Papa.unparse(csvData);
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'data.csv';
            
            // For Safari compatibility
            if (navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1) {
                link.target = '_blank';
                link.setAttribute('target', '_blank');
            }
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 100);
        } catch (err) {
            Toast.error('导出失败，请稍后重试');
            console.error('Export failed:', err);
        }
    };

    const activeTabData = tabData[activeTabKey] || createInitialTabState();
    const activeFilter = activeTabData.filter || createDefaultFilter();
    const dayData = activeTabData.daily || [];
    const totals = activeTabData.totals || getEmptyTotals();
    const quickRanges = [
        { key: 'today', label: '今天' },
        { key: '7d', label: '近 7 天' },
        { key: '30d', label: '近 30 天' },
        { key: '90d', label: '近 90 天' },
        { key: 'all', label: '全部' },
    ];

    const renderContent = () => (
        <>
            <Card style={{ marginTop: 24 }}>
                <Input
                    showClear
                    value={apikey}
                    onChange={(value) => setAPIKey(value)}
                    placeholder="请输入要查询的令牌 sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    prefix={<IconSearch />}
                    suffix={
                        <Button
                            type='primary'
                            theme="solid"
                            onClick={fetchData}
                            loading={loading}
                            disabled={apikey === ''}
                        >
                            查询
                        </Button>
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            fetchData();
                        }
                    }}
                />
            </Card>
            <Card style={{ marginTop: 24 }}>
                <Collapse activeKey={activeKeys} onChange={(keys) => setActiveKeys(keys)}>
                    {process.env.REACT_APP_SHOW_BALANCE === "true" && (
                        <Panel
                            header="令牌信息"
                            itemKey="1"
                            extra={
                                <Button icon={<IconCopy />} theme='borderless' type='primary' onClick={(e) => copyTokenInfo(e)} disabled={!activeTabData.tokenValid}>
                                    复制令牌信息
                                </Button>
                            }
                        >
                            <Spin spinning={loading}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">
                                        令牌名称：{activeTabData.tokenName || '未知'}
                                    </Text>
                                    <br /><br />
                                    <Text type="secondary">
                                        令牌总额：{activeTabData.unlimitedQuota ? "无限" : !activeTabData.tokenValid ? "未知" : renderQuota(activeTabData.totalGranted, 3)}
                                    </Text>
                                    <br /><br />
                                    <Text type="secondary">
                                        剩余额度：{activeTabData.unlimitedQuota ? "无限制" : !activeTabData.tokenValid ? "未知" : renderQuota(activeTabData.totalAvailable, 3)}
                                    </Text>
                                    <br /><br />
                                    <Text type="secondary">
                                        已用额度：{activeTabData.unlimitedQuota ? "不进行计算" : !activeTabData.tokenValid ? "未知" : renderQuota(activeTabData.totalUsed, 3)}
                                    </Text>
                                    <br /><br />
                                    <Text type="secondary">
                                        有效期至：{activeTabData.expiresAt === 0 ? '永不过期' : !activeTabData.tokenValid ? '未知' : renderTimestamp(activeTabData.expiresAt)}
                                    </Text>
                                </div>
                            </Spin>
                        </Panel>
                    )}
                    {process.env.REACT_APP_SHOW_DETAIL === "true" && (
                        <Panel
                            header="调用详情"
                            itemKey="2"
                            extra={
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Tag shape='circle' color='green' style={{ marginRight: 5 }}>计算汇率：$1 = 50 0000 tokens</Tag>
                                    <Button icon={<IconDownload />} theme='borderless' type='primary' onClick={(e) => exportCSV(e)} disabled={!activeTabData.tokenValid || activeTabData.logs.length === 0}>
                                        导出为CSV文件
                                    </Button>
                                </div>
                            }
                        >
                            <Spin spinning={loading}>
                                <Space wrap style={{ width: '100%', marginBottom: 16 }}>
                                    <DatePicker
                                        type="dateRange"
                                        value={activeFilter.dateRange}
                                        onChange={handleDateRangeChange}
                                        placeholder={['开始日期', '结束日期']}
                                    />
                                    {quickRanges.map((item) => (
                                        <Button
                                            key={item.key}
                                            size="small"
                                            theme={activeFilter.quickRange === item.key ? 'solid' : 'light'}
                                            onClick={() => setQuickRange(item.key)}
                                        >
                                            {item.label}
                                        </Button>
                                    ))}
                                    <Button type="primary" onClick={fetchData} loading={loading} disabled={apikey === ''}>
                                        查询
                                    </Button>
                                </Space>
                                {activeTabData.useFallback && (
                                    <Banner
                                        type="warning"
                                        description="当前后端版本不支持时间筛选接口，已回退到老接口，最多展示最近 1000 条调用"
                                        style={{ marginBottom: 16 }}
                                    />
                                )}
                                <Card style={{ marginBottom: 16 }}>
                                    <Row gutter={16}>
                                        <Col xs={24} sm={12} md={8} lg={4}>
                                            <Typography.Title heading={5} style={{ margin: 0 }}>
                                                总 Token： {formatTokens(totals.total_tokens)}
                                            </Typography.Title>
                                        </Col>
                                        <Col xs={12} sm={12} md={8} lg={4}>输入：{formatTokens(totals.prompt_tokens)}</Col>
                                        <Col xs={12} sm={12} md={8} lg={4}>输出：{formatTokens(totals.completion_tokens)}</Col>
                                        <Col xs={12} sm={12} md={8} lg={4}>缓存：{formatTokens(totals.cache_tokens)}</Col>
                                        <Col xs={12} sm={12} md={8} lg={4}>调用次数：{totals.calls || 0}</Col>
                                        <Col xs={24} sm={12} md={8} lg={4}>花费：${(totals.cost || 0).toFixed(6)}</Col>
                                    </Row>
                                </Card>
                                <div style={{ marginBottom: 24 }}>
                                    <Typography.Title heading={6} style={{ marginBottom: 8 }}>每日 Token 用量</Typography.Title>
                                    {dayData.length === 0 ? (
                                        <Typography.Text type="secondary">该时间段内暂无数据</Typography.Text>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={260}>
                                            <LineChart data={dayData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                                <YAxis tickFormatter={(v) => `${(v / 10000).toFixed(1)}万`} tick={{ fontSize: 11 }} />
                                                <RechartsTooltip formatter={(value, name) => [`${(value / 10000).toFixed(2)}万`, name]} />
                                                <Legend onClick={toggleLine(setHiddenLinesDetailChart)} formatter={legendFormatter(hiddenLinesDetailChart)} />
                                                <Line type="monotone" dataKey="total_tokens" name="总 Tokens" stroke="#4a90e2" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} hide={!!hiddenLinesDetailChart.total_tokens} />
                                                <Line type="monotone" dataKey="prompt_tokens" name="输入 Tokens" stroke="#52c41a" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3 }} activeDot={{ r: 5 }} hide={!!hiddenLinesDetailChart.prompt_tokens} />
                                                <Line type="monotone" dataKey="completion_tokens" name="输出 Tokens" stroke="#fa8c16" strokeWidth={2} strokeDasharray="4 2" dot={{ r: 3 }} activeDot={{ r: 5 }} hide={!!hiddenLinesDetailChart.completion_tokens} />
                                                <Line type="monotone" dataKey="cache_tokens" name="缓存 Tokens" stroke="#13c2c2" strokeWidth={2} strokeDasharray="2 2" dot={{ r: 3 }} activeDot={{ r: 5 }} hide={!!hiddenLinesDetailChart.cache_tokens} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                                <Table
                                    columns={columns}
                                    dataSource={activeTabData.logs}
                                    pagination={{
                                        currentPage: activeTabData.logsPage || 1,
                                        pageSize: activeTabData.logsPageSize || 10,
                                        hideOnSinglePage: false,
                                        showSizeChanger: true,
                                        pageSizeOpts: [10, 20, 50, 100],
                                        showTotal: (total) => `共 ${total} 条`,
                                        showQuickJumper: true,
                                        total: activeTabData.useFallback
                                            ? activeTabData.logs.length
                                            : (activeTabData.logsTotal || 0),
                                        onPageChange: (page) => loadLogsPage(page, activeTabData.logsPageSize || 10),
                                        onPageSizeChange: (newSize) => loadLogsPage(1, newSize),
                                        style: { marginTop: 12 },
                                    }}
                                />
                            </Spin>
                        </Panel>
                    )}
                    {process.env.REACT_APP_SHOW_DETAIL === "true" && (
                        <Panel header="使用统计（按天汇聚）" itemKey="3">
                            <Spin spinning={loading}>
                                {dayData.length === 0 ? (
                                    <Typography.Text type="secondary">暂无数据</Typography.Text>
                                ) : (
                                    <div>
                                        <Typography.Title heading={6} style={{ marginBottom: 8 }}>每日花费（美元）</Typography.Title>
                                        <ResponsiveContainer width="100%" height={260}>
                                            <BarChart data={dayData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                                <YAxis tickFormatter={(v) => `$${v.toFixed(6)}`} width={90} tick={{ fontSize: 11 }} />
                                                <RechartsTooltip formatter={(value) => [`$${value.toFixed(6)}`, '花费']} />
                                                <Legend />
                                                <Bar dataKey="cost" name="花费($)" fill="#4a90e2" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                        <Typography.Title heading={6} style={{ marginTop: 24, marginBottom: 8 }}>每日 Token 用量</Typography.Title>
                                        <ResponsiveContainer width="100%" height={260}>
                                            <LineChart data={dayData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                                <YAxis tickFormatter={(v) => `${(v / 10000).toFixed(1)}万`} tick={{ fontSize: 11 }} />
                                                <RechartsTooltip formatter={(value, name) => [`${(value / 10000).toFixed(2)}万`, name]} />
                                                <Legend onClick={toggleLine(setHiddenLinesStatsChart)} formatter={legendFormatter(hiddenLinesStatsChart)} />
                                                <Line type="monotone" dataKey="total_tokens" name="总 Tokens" stroke="#4a90e2" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} hide={!!hiddenLinesStatsChart.total_tokens} />
                                                <Line type="monotone" dataKey="prompt_tokens" name="输入 Tokens" stroke="#52c41a" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3 }} activeDot={{ r: 5 }} hide={!!hiddenLinesStatsChart.prompt_tokens} />
                                                <Line type="monotone" dataKey="completion_tokens" name="输出 Tokens" stroke="#fa8c16" strokeWidth={2} strokeDasharray="4 2" dot={{ r: 3 }} activeDot={{ r: 5 }} hide={!!hiddenLinesStatsChart.completion_tokens} />
                                                <Line type="monotone" dataKey="cache_tokens" name="缓存 Tokens" stroke="#13c2c2" strokeWidth={2} strokeDasharray="2 2" dot={{ r: 3 }} activeDot={{ r: 5 }} hide={!!hiddenLinesStatsChart.cache_tokens} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                        <Typography.Title heading={6} style={{ marginTop: 24, marginBottom: 8 }}>每日调用次数</Typography.Title>
                                        <ResponsiveContainer width="100%" height={220}>
                                            <BarChart data={dayData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                                <RechartsTooltip />
                                                <Legend />
                                                <Bar dataKey="calls" name="调用次数" fill="#722ed1" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </Spin>
                        </Panel>
                    )}
                </Collapse>
            </Card>
        </>
    );

    return (
        <>
            {Object.keys(baseUrls).length > 1 ? (
                <Tabs type="line" onChange={handleTabChange}>
                    {Object.entries(baseUrls).map(([key, url]) => (
                        <TabPane tab={key} itemKey={key} key={key}>
                            {renderContent()}
                        </TabPane>
                    ))}
                </Tabs>
            ) : (
                renderContent()
            )}
        </>
    );
};

export default LogsTable;
